import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import ShareMenu from "@/components/ShareMenu";
import { ChevronLeft, ChevronRight, MessageCircle, BadgePercent, Images, Megaphone, ArrowRight } from "lucide-react";

const KIND_META = {
  offer: { icon: BadgePercent, label: "Offer", chip: "bg-brand-terracotta" },
  carousel: { icon: Images, label: "Featured", chip: "bg-brand-emerald" },
  card: { icon: Megaphone, label: "Announcement", chip: "bg-brand-lease" },
};

export default function HomeShowcase() {
  const { settings, feature } = useSettings();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [canScroll, setCanScroll] = useState(false);
  const scrollRef = useRef(null);
  const waNumber = (settings?.whatsapp_number || "").replace(/[^0-9]/g, "");

  useEffect(() => {
    const check = () => {
      const el = scrollRef.current;
      setCanScroll(!!el && el.scrollWidth > el.clientWidth + 4);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [items]);

  useEffect(() => {
    const loc = localStorage.getItem("mp_area") || "";
    const calls = [];
    if (feature("offers")) calls.push(api.get("/offers/active").then((r) => (r.data.offers || []).map((o) => ({
      id: `offer-${o.id}`, kind: "offer", title: o.title, subtitle: o.discount || o.description || "",
      image: o.banner ? fileUrl(o.banner) : "", bg: "#c1502e",
    }))).catch(() => []));
    if (feature("carousel")) calls.push(api.get("/carousel").then((r) => (r.data.banners || []).map((b) => ({
      id: `carousel-${b.id}`, kind: "carousel", title: b.title || "Featured", subtitle: "",
      image: b.image ? fileUrl(b.image) : "", bg: "#1a5f5a",
    }))).catch(() => []));
    if (feature("card_banners")) calls.push(api.get("/card-banners", { params: loc ? { location: loc } : {} }).then((r) => (r.data.banners || []).map((b) => ({
      id: `card-${b.id}`, kind: "card", title: b.heading, subtitle: b.description || "",
      image: b.image ? fileUrl(b.image) : "", bg: b.bg_color || "#1a5f5a",
    }))).catch(() => []));
    if (calls.length === 0) { setItems([]); return; }
    Promise.all(calls).then((groups) => setItems(groups.flat()));
    // eslint-disable-next-line
  }, [settings.features, settings.whatsapp_number]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: dir * 240, behavior: "smooth" });
  };

  const enquire = (item) => {
    if (!waNumber) { navigate("/browse"); return; }
    const label = KIND_META[item.kind]?.label || "listing";
    const text = `Hi, I'm interested in your ${label.toLowerCase()}:\n*${item.title}*\n\nPlease share more details.`;
    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`, "_blank");
  };

  if (items.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 sm:pt-12" data-testid="home-showcase">
      <div className="flex items-end justify-between mb-5 gap-3">
        <div>
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-terracotta">
            <Megaphone className="w-4 h-4" /> What's New
          </span>
          <h2 className="font-heading font-bold text-brand-ink text-2xl sm:text-3xl tracking-tight mt-1.5">Offers & Highlights</h2>
        </div>
        {canScroll && (
          <div className="flex items-center gap-2">
            <button onClick={() => scrollBy(-1)} data-testid="showcase-prev" aria-label="Previous"
              className="w-9 h-9 rounded-full border border-brand-line bg-card hover:bg-secondary flex items-center justify-center text-brand-ink transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => scrollBy(1)} data-testid="showcase-next" aria-label="Next"
              className="w-9 h-9 rounded-full border border-brand-line bg-card hover:bg-secondary flex items-center justify-center text-brand-ink transition-colors">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      <div ref={scrollRef} className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-1 px-1 scroll-smooth" style={{ WebkitOverflowScrolling: "touch" }}>
        {items.map((item) => {
          const meta = KIND_META[item.kind] || KIND_META.card;
          const Icon = meta.icon;
          return (
            <div key={item.id} data-testid={`showcase-card-${item.id}`}
              className="snap-start shrink-0 w-[190px] sm:w-[210px] bg-card border border-brand-line rounded-2xl overflow-hidden flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div onClick={() => navigate("/browse")} className="cursor-pointer relative aspect-[16/9] overflow-hidden" style={{ backgroundColor: item.bg }} data-testid={`showcase-open-${item.id}`}>
                {item.image ? (
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/90"><Icon className="w-10 h-10" /></div>
                )}
                <span className={`absolute top-2.5 left-2.5 ${meta.chip} text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow`}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
              </div>
              <div className="p-3 flex flex-col flex-1">
                <h3 className="font-heading font-semibold text-brand-ink text-sm leading-snug line-clamp-1">{item.title}</h3>
                {item.subtitle && <p className="text-xs text-brand-muted mt-0.5 line-clamp-2 flex-1">{item.subtitle}</p>}
                <div className="mt-2.5 pt-2.5 border-t border-brand-line flex items-center justify-between gap-2">
                  <button onClick={() => navigate("/browse")} data-testid={`showcase-browse-${item.id}`}
                    className="text-xs font-medium text-brand-emerald hover:underline flex items-center gap-1">
                    View <ArrowRight className="w-3 h-3" />
                  </button>
                  <div className="flex items-center gap-1.5">
                    {feature("whatsapp_enquiry") && waNumber && (
                      <button onClick={() => enquire(item)} data-testid={`showcase-wa-${item.id}`} title="Enquire on WhatsApp"
                        className="w-7 h-7 rounded-full flex items-center justify-center bg-brand-lease/10 text-brand-lease hover:bg-brand-lease hover:text-white transition-colors">
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <ShareMenu title={item.title} url={`${window.location.origin}/browse`} testid={`showcase-share-${item.id}`} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
